import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Routes, Route } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RolePill, Empty, StatCard } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, DollarSign, Palette, Settings, Plus, X, Package, GitPullRequest, CheckCircle2 } from "lucide-react";

const FIRM_ROLES = ["tenant_admin", "partner", "manager", "staff"];
const CLIENT_ROLES = ["client_admin", "client_user"];

export default function AdminConsole() {
  return (
    <Routes>
      <Route index element={<UsersView />} />
      <Route path="branding" element={<Branding />} />
      <Route path="config" element={<Config />} />
      <Route path="plans" element={<PlansRequests />} />
      <Route path="billing" element={<Billing />} />
    </Routes>
  );
}

const MOD_LABEL = { module_1: "Data Collection" };
const MOD_COLOR = { module_1: "#4F46E5" };

function PlansRequests() {
  const [plans, setPlans] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => {
    api.get("/tenant/plans").then((r) => setPlans(r.data));
    api.get("/tenant/plan-requests").then((r) => setReqs(r.data));
  };
  useEffect(() => { load(); }, []);
  return (
    <div>
      <Header title="Plans & Requests" sub="Browse available plans and request a change for either module." />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
        {plans.map((p) => (
          <div key={p.id} data-testid={`tenant-plan-${p.id}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="overline" style={{ color: MOD_COLOR[p.module] }}>{MOD_LABEL[p.module]}</div>
            <div className="mt-1 font-heading text-lg font-bold">{p.name}</div>
            <div className="mt-2 text-2xl font-extrabold">${p.priceMonthly}<span className="text-sm font-normal text-slate-400">/mo</span></div>
            <ul className="mt-3 space-y-1.5">{(p.features || []).slice(0, 4).map((f, i) => <li key={`${p.id}-${i}`} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: MOD_COLOR[p.module] }} />{f}</li>)}</ul>
            <Button className="mt-4 w-full" variant="outline" data-testid={`request-plan-${p.id}`} onClick={() => setModal(p)}>Request this plan</Button>
          </div>
        ))}
      </div>
      <div className="overline text-azx-admin mb-2">My plan requests</div>
      {reqs.length === 0 ? <Empty icon={GitPullRequest} title="No requests yet" /> : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 text-left">Plan</th><th className="px-4 py-3 text-left">Cycle</th><th className="px-4 py-3 text-left">Notes</th><th className="px-4 py-3 text-left">Requested</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.id} data-testid={`my-request-${r.id}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-3 font-medium">{r.plan_name}</td><td className="px-4 py-3 capitalize">{r.billing_cycle}</td>
                  <td className="px-4 py-3 text-slate-500">{r.notes || "—"}</td><td className="px-4 py-3 text-xs text-slate-400">{new Date(r.requested_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <RequestModal plan={modal} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}

function RequestModal({ plan, onClose, onDone }) {
  const [cycle, setCycle] = useState("monthly");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try {
      await api.post("/tenant/plan-requests", { module: plan.module, requested_plan_id: plan.id, billing_cycle: cycle, notes });
      toast.success("Plan change requested — awaiting platform approval"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Request {plan.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Billing cycle</Label>
            <Select value={cycle} onValueChange={setCycle}><SelectTrigger className="mt-1.5" data-testid="req-cycle"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly (${plan.priceMonthly}/mo)</SelectItem><SelectItem value="annual">Annual (${plan.priceAnnual}/yr)</SelectItem></SelectContent></Select>
          </div>
          <div><Label>Notes (optional)</Label><Textarea data-testid="req-notes" className="mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="req-submit" className="bg-azx-admin hover:bg-slate-600">Submit request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Header({ title, sub, action }) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <div className="overline text-azx-admin">Firm Admin</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{title}</h1>
        {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function UsersView() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("firm");
  const [form, setForm] = useState({ name: "", email: "", roles: [] });
  const load = useCallback(() => api.get("/tenant/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const roleOptions = kind === "client" ? CLIENT_ROLES : FIRM_ROLES;
  const openDialog = (k) => { setKind(k); setForm({ name: "", email: "", roles: [] }); setOpen(true); };

  const toggle = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const create = async () => {
    if (!form.name || !form.email || form.roles.length === 0) return toast.error("Name, email and at least one role required");
    try {
      await api.post("/tenant/users", form);
      toast.success("User created (temp password: Demo@123)");
      setOpen(false); setForm({ name: "", email: "", roles: [] }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (u) => {
    try { await api.delete(`/tenant/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <Header title="Users & Roles" sub="RBAC is enforced at the API — role changes take effect immediately."
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="add-firm-user-btn" onClick={() => openDialog("firm")} className="bg-azx-admin hover:bg-slate-600"><UserPlus className="mr-2 h-4 w-4" /> Add firm user</Button>
            <Button data-testid="add-client-user-btn" onClick={() => openDialog("client")} variant="outline" className="border-azx-admin text-azx-admin hover:bg-slate-100 dark:hover:bg-slate-800"><UserPlus className="mr-2 h-4 w-4" /> Add client user</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent>
                <DialogHeader><DialogTitle>{kind === "client" ? "Add client user" : "Add firm user"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Name</Label><Input data-testid="user-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="user-email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div>
                    <Label>{kind === "client" ? "Client roles" : "Firm roles"}</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {roleOptions.map((r) => (
                        <label key={r} className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-2 text-sm cursor-pointer">
                          <Checkbox checked={form.roles.includes(r)} onCheckedChange={() => toggle(r)} data-testid={`role-${r}`} />
                          {r.replace(/_/g, " ")}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={create} data-testid="user-save">Create user</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        } />

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Roles</th><th className="px-4 py-3 text-left">Last login</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name} {u.external && <span className="ml-1 text-[10px] font-bold text-indigo-500">EXTERNAL</span>}</div>
                  <div className="text-xs text-slate-400 font-mono">{u.email}</div>
                </td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <RolePill key={r} role={r} />)}</div></td>
                <td className="px-4 py-3 text-xs text-slate-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" className="text-red-500" data-testid={`delete-user-${u.id}`} onClick={() => del(u)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Branding() {
  const { loadMe, session } = useAuth();
  const [b, setB] = useState({ primary: "", logo_text: "", name: "" });
  useEffect(() => {
    api.get("/tenant/billing").then((r) => setB({ primary: r.data.branding?.primary || "#2563EB", logo_text: r.data.branding?.logo_text || "", name: session?.tenant?.name || "" }));
  }, [session]);
  const save = async () => {
    try { await api.patch("/tenant/branding", b); await loadMe(); toast.success("Branding saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-lg">
      <Header title="Branding" sub="Customize how your workspace looks." />
      <div className="space-y-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div><Label>Organization name</Label><Input data-testid="brand-name" className="mt-1.5" value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} /></div>
        <div><Label>Logo text (2 letters)</Label><Input data-testid="brand-logo" maxLength={2} className="mt-1.5 uppercase" value={b.logo_text} onChange={(e) => setB({ ...b, logo_text: e.target.value.toUpperCase() })} /></div>
        <div>
          <Label>Primary color</Label>
          <div className="mt-1.5 flex items-center gap-3">
            <input type="color" data-testid="brand-color" value={b.primary} onChange={(e) => setB({ ...b, primary: e.target.value })} className="h-10 w-16 rounded border" />
            <span className="font-mono text-sm">{b.primary}</span>
          </div>
        </div>
        <Button onClick={save} data-testid="brand-save" style={{ backgroundColor: b.primary }}>Save branding</Button>
      </div>
    </div>
  );
}

function Config() {
  const [cfg, setCfg] = useState({ statuses: [], default_phases: [] });
  const [newStatus, setNewStatus] = useState("");
  const [newPhase, setNewPhase] = useState("");
  useEffect(() => { api.get("/tenant/config").then((r) => setCfg(r.data)); }, []);
  const save = async (next) => {
    setCfg(next);
    try { await api.patch("/tenant/config", next); toast.success("Configuration saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const List = ({ items, kind, val, setVal, testid }) => (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <div className="font-heading font-semibold mb-3 capitalize">{kind}</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-sm">
            {s}<button onClick={() => save({ ...cfg, [kind === "statuses" ? "statuses" : "default_phases"]: items.filter((_, j) => j !== i) })}><X className="h-3.5 w-3.5 text-slate-400" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={val} onChange={(e) => setVal(e.target.value)} placeholder={`New ${kind === "statuses" ? "status" : "phase"}`} />
        <Button variant="outline" onClick={() => { if (val) { save({ ...cfg, [kind === "statuses" ? "statuses" : "default_phases"]: [...items, val] }); setVal(""); } }}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
  return (
    <div className="max-w-2xl">
      <Header title="Configuration" sub="Tenant-wide statuses and default engagement phases." />
      <div className="grid gap-4">
        <List items={cfg.statuses || []} kind="statuses" val={newStatus} setVal={setNewStatus} testid="new-status" />
        <List items={cfg.default_phases || []} kind="phases" val={newPhase} setVal={setNewPhase} testid="new-phase" />
      </div>
    </div>
  );
}

function Billing() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/tenant/billing").then((r) => setB(r.data)); }, []);
  if (!b) return null;
  return (
    <div>
      <Header title="Billing" sub="What your organization currently pays for." />
      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <StatCard label="Seats used" value={b.seats_used} icon={Users} accent="#475569" />
        <StatCard label="Region" value={b.region} icon={Settings} accent="#2563EB" />
        <StatCard label="Modules" value={Object.values(b.subscriptions).filter((s) => s.status === "active").length} icon={DollarSign} accent="#059669" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Object.entries(b.subscriptions).map(([mod, s]) => (
          <div key={mod} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="overline" style={{ color: "#4F46E5" }}>Data Collection</div>
            <div className="mt-1 font-heading text-lg font-bold capitalize">{s.plan} plan</div>
            <div className="mt-1 text-sm capitalize text-slate-500">Status: <b className={s.status === "active" ? "text-emerald-600" : "text-amber-600"}>{s.status}</b></div>
            {s.renews_at && <div className="text-xs text-slate-400 mt-1">Renews {new Date(s.renews_at).toLocaleDateString()}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
